import { audio } from '../audio/audio.js';
import { menuIconElement } from '../content/menu-icons.js';
import { deleteEverything, exportAll } from '../save/db.js';
import { el, timeout } from './dom.js';
import type { Settings } from '../core/save/model.js';
import type { Screen, ScreenContext } from './router.js';

/**
 * The grown-ups' screen: the one place in the game with words in it.
 *
 * Everything else in this build is held to the wordless rule by test, because she cannot read and copy she
 * cannot read is either decoration or a dead end. This screen is the deliberate exception, and the exception
 * is the *point* of it twice over:
 *
 *  1. **It is for whoever is looking after her**, so plain sentences are the clearest thing it can say. The
 *     install state in particular has no picture: "no service worker on this address" is a fact about a
 *     browser, and a pictogram of it would be a puzzle.
 *  2. **It is the second half of the lock.** A near-black screen of small grey type and square-cornered
 *     buttons is a screen she does not recognise as part of her game, and a screen she does not recognise is
 *     a screen she leaves. That is worth more than any password would be at her age - and it is why nothing
 *     in here uses `.card`, `bigButton`, a paw, or a colour from the game's palette.
 *
 * Getting in takes two things she cannot do on purpose: a three-second hold in the corner of the Lookout
 * (`gate` in `hub-screen.ts`), and then the sequence below. The wordless sweep in
 * `tests/loop-walkthrough.test.ts` names this screen as its one exemption, by phase, so easing the rule here
 * cannot ease it anywhere else.
 */

/** How many pads the sequence is played on, and how many presses it takes to get in. */
const PADS = 4;
const SEQUENCE_LENGTH = 3;

/** How long each pad stays lit while the sequence is shown, and the gap between two. */
const FLASH_MS = 520;
const GAP_MS = 220;

/** The volume steps the two rows offer. Five is enough to be useful and few enough to be a row of squares. */
const LEVELS: readonly number[] = [0, 0.25, 0.5, 0.75, 1];

export interface ParentScreenOptions {
  /**
   * The sequence to ask for. Injected for tests; in the game it is three of the four pads in a random
   * order, chosen fresh on every entry.
   */
  readonly sequence?: readonly number[];
  /**
   * Whether to play the sequence back before accepting presses. Tests pass `false` to skip the timers and
   * arm the pads at once; nothing in the game does.
   */
  readonly reveal?: boolean;
  /** Injected for tests, which have no IndexedDB and must not reload the runner. */
  readonly wipe?: () => Promise<void>;
  readonly reload?: () => void;
  readonly saveCopy?: () => Promise<void>;
  /** Injected for tests: jsdom has no service worker and no display-mode media query. */
  readonly install?: () => InstallState;
}

/** What the tablet thinks of this build. Every field is a fact the README's install steps turn on. */
export interface InstallState {
  /** Whether the origin counts as secure. `http://192.168.x.x` does not, which is the whole problem. */
  readonly secure: boolean;
  /** Whether service workers are exposed at all. On an insecure origin they are not - not even as an API. */
  readonly supported: boolean;
  /** Whether one is actually running this page, which is what "works with the laptop off" means. */
  readonly active: boolean;
  /** Whether the game is running from the home-screen icon rather than in a browser tab. */
  readonly installed: boolean;
  readonly origin: string;
}

export function createParentScreen(options: ParentScreenOptions = {}): Screen {
  const install = options.install ?? readInstallState;
  const wipe = options.wipe ?? deleteEverything;
  const reload = options.reload ?? (() => location.reload());
  const saveCopy = options.saveCopy ?? downloadEverything;

  let disposed = false;
  const disposers: (() => void)[] = [];

  return {
    mount(root, ctx) {
      const body = el('div', { className: 'parent-body' });
      const screen = el('div', {
        className: 'screen screen--parent',
        children: [body],
      });
      root.append(screen);

      lock(body, ctx, () => {
        if (disposed) return;
        settings(body, ctx);
      });
    },

    unmount() {
      disposed = true;
      for (const dispose of disposers) dispose();
      disposers.length = 0;
    },
  };

  /* ------------------------------------------------------------------ the lock */

  /**
   * Four pads; three of them light in order, once; press them back in that order to get in.
   *
   * **One wrong press leaves.** Not "try again": she is the one who gets it wrong, and a screen that keeps
   * her there re-rolling a puzzle is a screen she stays on pressing things. A parent who watched the
   * sequence gets it first time, and can hold the corner again if they looked away.
   *
   * **Presses during the playback are ignored rather than wrong**, so a parent tapping along with the
   * flashes does not get thrown out by their own enthusiasm.
   */
  function lock(host: HTMLElement, ctx: ScreenContext, onOpen: () => void): void {
    const wanted = options.sequence ?? randomSequence();
    let armed = options.reveal === false;
    let at = 0;

    const pads = Array.from({ length: PADS }, (_, index) => {
      const pad = el('button', { className: 'parent-pad' });
      pad.type = 'button';
      pad.title = `pad ${index + 1}`;
      // The paw the sequence is played on. A missing file leaves a plain square, which the flash still
      // lights - the lock must not depend on the content pack being there.
      void menuIconElement('paw').then((paw) => {
        if (!disposed && paw !== null) pad.append(paw);
      });
      pad.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        if (!armed) return;
        if (wanted[at] !== index) {
          ctx.dispatch({ type: 'BACK' });
          return;
        }
        at += 1;
        pad.classList.add('is-hit');
        if (at >= wanted.length) onOpen();
      });
      return pad;
    });

    host.replaceChildren(
      el('p', { className: 'parent-lead', text: 'Grown-ups' }),
      el('p', {
        className: 'parent-note',
        text: 'Press the paws in the order they light up.',
      }),
      el('div', { className: 'parent-pads', children: pads }),
      leaveButton(ctx),
    );

    if (armed) return;

    /*
     * The playback, one timer per flash rather than an interval.
     *
     * Every one of them is registered for disposal: the screen can be left mid-sequence - by the way out
     * below, or by a wrong press - and a flash landing on a torn-down screen would touch a detached node,
     * while the arming timer landing late would arm a lock nobody is looking at.
     */
    wanted.forEach((padIndex, step) => {
      const at0 = step * (FLASH_MS + GAP_MS);
      const pad = pads[padIndex];
      if (pad === undefined) return;
      disposers.push(timeout(at0, () => pad.classList.add('is-lit')));
      disposers.push(timeout(at0 + FLASH_MS, () => pad.classList.remove('is-lit')));
    });
    disposers.push(
      timeout(wanted.length * (FLASH_MS + GAP_MS), () => {
        armed = true;
        host.querySelector('.parent-pads')?.classList.add('is-armed');
      }),
    );
  }

  /* -------------------------------------------------------------- the settings */

  function settings(host: HTMLElement, ctx: ScreenContext): void {
    const state = install();

    host.replaceChildren(
      /*
       * The way out is at the *top* here, beside the heading, and at the bottom on the lock.
       *
       * The settings are longer than a 1024x768 window, so a leave button after them is a leave button
       * below the fold - and the one control whoever opened this screen is certain to want is the one that
       * closes it. On the lock there is nothing to scroll past, so it reads better underneath.
       */
      el('div', {
        className: 'parent-head',
        children: [el('p', { className: 'parent-lead', text: 'Grown-ups' }), leaveButton(ctx)],
      }),

      section('Sound', [
        volumeRow('Sound effects', 'sfxVolume', ctx),
        volumeRow('Music', 'musicVolume', ctx),
        el('p', {
          className: 'parent-hint',
          // Honest rather than tidy: the bus is wired and nothing plays through it yet, and a slider that
          // does nothing audible is worse than a slider with a sentence under it.
          text: 'There is no music yet. This sets the level for when a sound pack is installed.',
        }),
      ]),

      section('This tablet', [installLines(state)]),

      section('Her cars', [
        actionButton('Save a copy of everything', () => {
          void saveCopy().catch((error: unknown) => {
            console.error('[pawp] could not save a copy', error);
          });
        }),
        el('p', {
          className: 'parent-hint',
          text: 'One file with every car in it, and the pictures. Browser storage can be evicted; this is the only copy that is not in it.',
        }),
        deleteRow(),
      ]),
    );
  }

  /**
   * Volume as five squares rather than a slider.
   *
   * A range input is the obvious control and the wrong one here: the whole document sets
   * `touch-action: none` to stop a stray finger panning the game, dragging a thumb is the one gesture that
   * needs it back, and the parent is holding a tablet a three-year-old is trying to take. Five steps is
   * every level anybody actually wants, and each one is a press that lands.
   *
   * Each press applies to the running audio *and* to the save, and plays the press sound - which is the
   * only way to hear what "50%" means on the effects row while setting it.
   */
  function volumeRow(label: string, key: 'musicVolume' | 'sfxVolume', ctx: ScreenContext): HTMLElement {
    const buttons: HTMLButtonElement[] = [];

    const mark = (chosen: number): void => {
      for (const [index, button] of buttons.entries()) {
        button.classList.toggle('is-on', index === chosen);
      }
    };

    for (const [index, level] of LEVELS.entries()) {
      const button = el('button', {
        className: 'parent-step',
        text: `${Math.round(level * 100)}%`,
      });
      button.type = 'button';
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        ctx.updateSave((save) => ({ ...save, settings: { ...save.settings, [key]: level } }));
        // Read back rather than assumed: the other row's level has to survive setting this one.
        const now: Settings = ctx.currentSave().settings;
        audio.setVolumes(now.musicVolume, now.sfxVolume);
        mark(index);
        // After the change, so it is heard at the level just chosen. Silent at 0%, which is the answer.
        audio.press();
      });
      buttons.push(button);
    }

    mark(nearestLevel(ctx.currentSave().settings[key]));
    return el('div', {
      className: 'parent-row',
      children: [el('span', { className: 'parent-label', text: label }), ...buttons],
    });
  }

  /**
   * Whether this tablet will run the game with the laptop off, in the order the answer depends on.
   *
   * This is the screen the README's install section has always needed. Every one of those steps fails
   * silently on the device - an insecure origin does not merely refuse to register a service worker, it does
   * not expose the API at all, and Chrome's own menu says "Add shortcut" instead of "Install" with no
   * explanation. Four lines here beat a paragraph in a document nobody has on the tablet.
   */
  function installLines(state: InstallState): HTMLElement {
    const lines = [
      yesNo('Runs offline', state.active),
      yesNo('Opened from the home screen', state.installed),
      yesNo('Secure origin', state.secure),
      el('p', { className: 'parent-fact', text: state.origin }),
    ];

    if (!state.supported) {
      lines.push(
        el('p', {
          className: 'parent-hint',
          text: 'This address exposes no service worker, so the game cannot be installed from it. Trust the origin in chrome://flags (#unsafely-treat-insecure-origin-as-secure) or serve it over HTTPS - see the README.',
        }),
      );
    }

    return el('div', { className: 'parent-facts', children: lines });
  }

  /**
   * Delete everything, behind a second press.
   *
   * Her cars are the one unrecoverable thing in this game, and this is the only button in the build that can
   * take all of them at once - so it asks, the way the Garage's bin asks, and "keep everything" comes first
   * of the two, because the safe answer should be the one a hand reaches by habit. It wipes the whole
   * database rather than emptying the save: the thumbnails live in their own store, and a wipe that leaves a
   * dozen pictures of her cars behind is not the thing it says it is.
   */
  function deleteRow(): HTMLElement {
    const row = el('div', { className: 'parent-danger' });

    const confirm = el('div', {
      className: 'parent-confirm',
      children: [
        actionButton('Keep everything', () => row.classList.remove('is-confirming')),
        actionButton('Yes, delete it all', () => {
          void wipe()
            .catch((error: unknown) => {
              console.error('[pawp] could not delete everything', error);
            })
            .finally(() => reload());
        }, 'parent-button--danger'),
      ],
    });

    row.append(
      actionButton('Delete everything', () => row.classList.add('is-confirming')),
      el('p', {
        className: 'parent-hint',
        text: 'Every car, every prize, every setting. There is no undo and no backup afterwards.',
      }),
      confirm,
    );
    return row;
  }

  /** The way out. A word, not a paw: the paw is hers, and this screen is not. */
  function leaveButton(ctx: ScreenContext): HTMLElement {
    return actionButton(
      'Back to the game',
      () => ctx.dispatch({ type: 'BACK' }),
      'parent-button--leave',
    );
  }
}

function section(title: string, children: readonly HTMLElement[]): HTMLElement {
  return el('section', {
    className: 'parent-section',
    children: [el('h2', { className: 'parent-heading', text: title }), ...children],
  });
}

function actionButton(label: string, onPress: () => void, className = ''): HTMLButtonElement {
  const button = el('button', { className: `parent-button ${className}`.trim(), text: label });
  button.type = 'button';
  button.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    onPress();
  });
  return button;
}

function yesNo(label: string, value: boolean): HTMLElement {
  return el('p', {
    className: `parent-fact ${value ? 'is-yes' : 'is-no'}`,
    text: `${label}: ${value ? 'yes' : 'no'}`,
  });
}

/** Three of the four pads, in an order nobody has seen before. */
function randomSequence(): readonly number[] {
  const pads = [...Array(PADS).keys()];
  for (let i = pads.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = pads[i];
    const b = pads[j];
    if (a === undefined || b === undefined) continue;
    pads[i] = b;
    pads[j] = a;
  }
  return pads.slice(0, SEQUENCE_LENGTH);
}

/** Which of the five squares a saved volume belongs to - a save can hold any number in 0..1. */
function nearestLevel(value: number): number {
  let best = 0;
  for (const [index, level] of LEVELS.entries()) {
    if (Math.abs(level - value) < Math.abs((LEVELS[best] ?? 0) - value)) best = index;
  }
  return best;
}

/**
 * What the browser will admit to. Guarded throughout: this is a diagnostic screen, and it may not be the
 * thing that fails.
 */
function readInstallState(): InstallState {
  const supported = 'serviceWorker' in navigator;
  let installed = false;
  try {
    installed =
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      // iOS Safari's own flag; it has no display-mode media query in a home-screen app.
      (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    // No matchMedia. The answer is "cannot tell", which reads the same as "no" on this screen.
  }

  return {
    secure: window.isSecureContext === true,
    supported,
    active: supported && navigator.serviceWorker.controller !== null,
    installed,
    origin: location.origin,
  };
}

/**
 * Her whole garage, as one file on a real filesystem.
 *
 * JSON with the thumbnails inlined as data URLs rather than a zip: there is no zip in this build and no
 * package worth adding for one, and a single file is what somebody will actually keep. It is only ever
 * *written* here - reading one back in is a restore path, which is a bigger thing than a button and is not
 * built.
 */
async function downloadEverything(): Promise<void> {
  const { save, images } = await exportAll();

  const pictures: Record<string, string> = {};
  for (const [carId, blob] of images) pictures[carId] = await blobToDataUrl(blob);

  const file = new Blob([JSON.stringify({ save, pictures }, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(file);
  const link = el('a', { attrs: { download: `patrol-${stamp()}.json`, href: url } });
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on a timer rather than at once: Safari has been known to cancel a download whose blob URL is
  // released in the same task as the click.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('could not read a car picture'));
    reader.readAsDataURL(blob);
  });
}

/** `2026-07-29-1412`, so two copies made on one day do not collide in the downloads folder. */
function stamp(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`
  );
}
